import { useTranslation } from "react-i18next";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { SEO } from "@/components/SEO";
import { SEOBreadcrumb } from "@/components/SEOBreadcrumb";

export default function Terms() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO 
        title={t('legal.terms.title')}
        description={t('legal.terms.acceptanceContent')}
      />
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        <BrutalCard className="max-w-4xl mx-auto">
          <SEOBreadcrumb items={[{ label: t('legal.terms.breadcrumb') }]} align="left" />
          <h1 className="text-4xl font-heading font-bold mb-4">
            {t('legal.terms.title')}
          </h1>
          <p className="text-muted-foreground mb-8">
            {t('legal.terms.lastUpdated')}
          </p>
          
          <div className="space-y-8 text-foreground">
            {/* Titolare del Trattamento */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.terms.controller.title')}
              </h2>
              <p className="mb-4">{t('legal.terms.controller.text')}</p>
              <div className="bg-muted/50 border-2 border-dark p-5 shadow-brutal space-y-1">
                <p className="!mt-0"><strong>{t('legal.terms.controller.name')}</strong></p>
                <p>{t('legal.terms.controller.vat')}</p>
                <p>Email: <a href={`mailto:${t('legal.terms.controller.email')}`} className="text-primary underline">{t('legal.terms.controller.email')}</a></p>
              </div>
            </section>

            {/* 1. Accettazione */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                1. {t('legal.terms.acceptance')}
              </h2>
              <p>{t('legal.terms.acceptanceContent')}</p>
            </section>

            {/* 2. Definizioni */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                2. {t('legal.terms.definitions.title')}
              </h2>
              <p className="mb-4">{t('legal.terms.definitions.intro')}</p>
              <ul className="list-disc pl-6 space-y-2">
                {(t('legal.terms.definitions.list', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 3. Descrizione del Servizio */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                3. {t('legal.terms.service')}
              </h2>
              <p className="mb-4">{t('legal.terms.serviceIntro')}</p>
              <ul className="list-disc pl-6 space-y-2">
                {(t('legal.terms.serviceFeatures', { returnObjects: true }) as string[]).map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
            </section>

            {/* 4. Registrazione */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                4. {t('legal.terms.registration')}
              </h2>
              
              <h3 className="text-xl font-heading font-semibold mb-2">
                4.1 {t('legal.terms.registrationReq')}
              </h3>
              <p className="mb-4">{t('legal.terms.registrationReqText')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                4.2 {t('legal.terms.accountSecurity')}
              </h3>
              <p className="mb-4">{t('legal.terms.accountSecurityText')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                4.3 {t('legal.terms.professionalAccount')}
              </h3>
              <p>{t('legal.terms.professionalAccountText')}</p>
            </section>

            {/* 5. Abbonamenti e Pagamenti */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                5. {t('legal.terms.subscriptions.title')}
              </h2>
              
              <h3 className="text-xl font-heading font-semibold mb-2">
                5.1 {t('legal.terms.subscriptions.plans.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.subscriptions.plans.intro')}</p>
              
              <div className="overflow-x-auto mb-4">
                <table className="w-full border-2 border-dark">
                  <thead className="bg-muted">
                    <tr>
                      <th className="border-2 border-dark p-3 text-left font-heading">{t('legal.terms.subscriptions.plans.table.plan')}</th>
                      <th className="border-2 border-dark p-3 text-left font-heading">{t('legal.terms.subscriptions.plans.table.price')}</th>
                      <th className="border-2 border-dark p-3 text-left font-heading">{t('legal.terms.subscriptions.plans.table.renewal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border-2 border-dark p-3">{t('legal.terms.subscriptions.plans.base.name')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.subscriptions.plans.base.price')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.subscriptions.plans.base.renewal')}</td>
                    </tr>
                    <tr>
                      <td className="border-2 border-dark p-3">{t('legal.terms.subscriptions.plans.pro.name')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.subscriptions.plans.pro.price')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.subscriptions.plans.pro.renewal')}</td>
                    </tr>
                    <tr>
                      <td className="border-2 border-dark p-3">{t('legal.terms.subscriptions.plans.plus.name')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.subscriptions.plans.plus.price')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.subscriptions.plans.plus.renewal')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mb-4 text-sm text-muted-foreground">{t('legal.terms.subscriptions.plans.note')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                5.2 {t('legal.terms.subscriptions.payment.title')}
              </h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                {(t('legal.terms.subscriptions.payment.list', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>

              <h3 className="text-xl font-heading font-semibold mb-2">
                5.3 {t('legal.terms.subscriptions.renewal.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.subscriptions.renewal.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                5.4 {t('legal.terms.subscriptions.withdrawal.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.subscriptions.withdrawal.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                5.5 {t('legal.terms.subscriptions.upgrade.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.subscriptions.upgrade.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                5.6 {t('legal.terms.subscriptions.cancellation.title')}
              </h3>
              <p>{t('legal.terms.subscriptions.cancellation.text')}</p>
            </section>

            {/* 6. Pacchetti Visibilità */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                6. {t('legal.terms.visibility.title')}
              </h2>
              <p className="mb-4">{t('legal.terms.visibility.intro')}</p>
              
              <div className="overflow-x-auto mb-4">
                <table className="w-full border-2 border-dark">
                  <thead className="bg-muted">
                    <tr>
                      <th className="border-2 border-dark p-3 text-left font-heading">{t('legal.terms.visibility.table.package')}</th>
                      <th className="border-2 border-dark p-3 text-left font-heading">{t('legal.terms.visibility.table.price')}</th>
                      <th className="border-2 border-dark p-3 text-left font-heading">{t('legal.terms.visibility.table.duration')}</th>
                      <th className="border-2 border-dark p-3 text-left font-heading">{t('legal.terms.visibility.table.features')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border-2 border-dark p-3">{t('legal.terms.visibility.spotlight.name')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.visibility.spotlight.price')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.visibility.spotlight.duration')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.visibility.spotlight.features')}</td>
                    </tr>
                    <tr>
                      <td className="border-2 border-dark p-3">{t('legal.terms.visibility.socialBoost.name')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.visibility.socialBoost.price')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.visibility.socialBoost.duration')}</td>
                      <td className="border-2 border-dark p-3">{t('legal.terms.visibility.socialBoost.features')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="bg-destructive/10 border-2 border-dark p-4 shadow-brutal">
                <p className="m-0">{t('legal.terms.visibility.noRefund')}</p>
              </div>
            </section>

            {/* 7. Annunci di Lavoro */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                7. {t('legal.terms.jobCredits.title')}
              </h2>
              <p className="mb-4">{t('legal.terms.jobCredits.intro')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                7.1 {t('legal.terms.jobCredits.system.title')}
              </h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                {(t('legal.terms.jobCredits.system.list', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>

              <h3 className="text-xl font-heading font-semibold mb-2">
                7.2 {t('legal.terms.jobCredits.content.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.jobCredits.content.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                7.3 {t('legal.terms.jobCredits.responsibility.title')}
              </h3>
              <p>{t('legal.terms.jobCredits.responsibility.text')}</p>
            </section>

            {/* 8. Contenuti e Condotta */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                8. {t('legal.terms.conduct')}
              </h2>
              
              <h3 className="text-xl font-heading font-semibold mb-2">
                8.1 {t('legal.terms.publishedContent')}
              </h3>
              <p className="mb-4">{t('legal.terms.publishedContentText')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                8.2 {t('legal.terms.prohibitedContent')}
              </h3>
              <p className="mb-2">{t('legal.terms.prohibitedContentIntro')}</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                {(t('legal.terms.prohibitedContentList', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>

              <h3 className="text-xl font-heading font-semibold mb-2">
                8.3 {t('legal.terms.moderation')}
              </h3>
              <p>{t('legal.terms.moderationText')}</p>
            </section>

            {/* 9. Recensioni */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                9. {t('legal.terms.reviews.title')}
              </h2>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                {(t('legal.terms.reviews.list', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <p className="text-sm text-muted-foreground">{t('legal.terms.reviews.note')}</p>
            </section>

            {/* 10. Eventi */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                10. {t('legal.terms.events.title')}
              </h2>
              
              <h3 className="text-xl font-heading font-semibold mb-2">
                10.1 {t('legal.terms.events.organization.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.events.organization.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                10.2 {t('legal.terms.events.cancellation.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.events.cancellation.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                10.3 {t('legal.terms.events.conduct.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.events.conduct.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                10.4 {t('legal.terms.events.privacy.title')}
              </h3>
              <p>{t('legal.terms.events.privacy.text')}</p>
            </section>

            {/* 11. Affiliazione */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                11. {t('legal.terms.affiliate.title')}
              </h2>
              <p className="mb-4">{t('legal.terms.affiliate.intro')}</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                {(t('legal.terms.affiliate.list', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>

              <h3 className="text-xl font-heading font-semibold mb-2">
                11.1 {t('legal.terms.affiliate.prohibited.title')}
              </h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                {(t('legal.terms.affiliate.prohibited.list', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <p className="text-sm text-muted-foreground">{t('legal.terms.affiliate.note')}</p>
            </section>

            {/* 12. Programma Creator */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                12. {t('legal.terms.creator.title')}
              </h2>
              <p className="mb-4">{t('legal.terms.creator.intro')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                12.1 {t('legal.terms.creator.participation.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.creator.participation.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                12.2 {t('legal.terms.creator.approval.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.creator.approval.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                12.3 {t('legal.terms.creator.rewards.title')}
              </h3>
              <p className="mb-4">{t('legal.terms.creator.rewards.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                12.4 {t('legal.terms.creator.ip.title')}
              </h3>
              <p>{t('legal.terms.creator.ip.text')}</p>
            </section>

            {/* 13. Proprietà Intellettuale */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                13. {t('legal.terms.ip')}
              </h2>
              <p>{t('legal.terms.ipText')}</p>
            </section>

            {/* 14. Privacy */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                14. {t('legal.terms.privacy.title')}
              </h2>
              <p className="mb-4">{t('legal.terms.privacy.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2">
                {t('legal.terms.privacy.rights.title')}
              </h3>
              <p className="mb-2">{t('legal.terms.privacy.rights.intro')}</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                {(t('legal.terms.privacy.rights.list', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <p>{t('legal.terms.privacy.contact')}</p>
            </section>

            {/* 15. Limitazione Responsabilità */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                15. {t('legal.terms.liability')}
              </h2>
              <p className="mb-2">{t('legal.terms.liabilityIntro')}</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                {(t('legal.terms.liabilityList', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <p>{t('legal.terms.liabilityNote')}</p>
            </section>

            {/* 16. Indennizzo */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                16. {t('legal.terms.indemnification')}
              </h2>
              <p>{t('legal.terms.indemnificationText')}</p>
            </section>

            {/* 17. Modifiche */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                17. {t('legal.terms.modifications')}
              </h2>
              <p>{t('legal.terms.modificationsText')}</p>
            </section>

            {/* 18. Legge Applicabile */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                18. {t('legal.terms.law')}
              </h2>
              <p>{t('legal.terms.lawText')}</p>
            </section>

            {/* 19. Contatti */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                19. {t('legal.terms.contact')}
              </h2>
              <p className="mb-4">{t('legal.terms.contactIntro')}</p>
              <div className="bg-muted/50 border-2 border-dark p-5 shadow-brutal space-y-1">
                <p className="!mt-0"><strong>{t('legal.terms.contactBox.name')}</strong></p>
                <p>{t('legal.terms.contactBox.vat')}</p>
                <p>Email: <a href={`mailto:${t('legal.terms.contactBox.email')}`} className="text-primary underline">{t('legal.terms.contactBox.email')}</a></p>
              </div>
            </section>

            {/* Link Utili */}
            <section className="bg-muted/50 border-2 border-dark p-6 shadow-brutal mt-8">
              <h3 className="text-xl font-heading font-semibold mt-0 mb-0">
                {t('legal.terms.usefulLinks')}
              </h3>
              <ul className="list-none space-y-2 mt-3 mb-0">
                <li>
                  <a href="/privacy" className="text-primary underline">
                    {t('legal.privacy.title')}
                  </a>
                </li>
                <li>
                  <a href="/cookie-policy" className="text-primary underline">
                    {t('legal.cookies.title')}
                  </a>
                </li>
                <li>
                  <a href="/subscription-plans" className="text-primary underline">
                    {t('subscriptionPlans.heroTitle')}
                  </a>
                </li>
                <li>
                  <a href="/visibility-packages" className="text-primary underline">
                    {t('visibilityPackages.heroTitle')}
                  </a>
                </li>
              </ul>
            </section>
          </div>
        </BrutalCard>
      </main>

      <Footer />
    </div>
  );
}